/**
 * Calendly consultation CTAs from Contentful (showroom “Book a consultation” block).
 * @see CL-011 — validate / sanitize before rendering booking controls.
 */

import {urlHelper} from '@amblique/base/app/helpers/url-helper';
import {SHOWROOMS_LISTING_PATH} from '@amblique/king-living/app/constants/site-config';

export const CALENDLY_IN_PERSON_HASH = '#calendly-in-person';
export const CALENDLY_VIDEO_HASH = '#calendly-video';

/** App path for KINDW-1296 fallback when consultation booking cannot be resolved off showroom PDP. */
export const CALENDLY_FIND_SHOWROOM_APP_PATH = SHOWROOMS_LISTING_PATH;

/**
 * @typedef {{ type: 'hidden' }} ConsultationCalendlyCtaOutcomeHidden
 * @typedef {{ type: 'render', url: string | undefined }} ConsultationCalendlyCtaOutcomeRender
 * @typedef {{ type: 'findShowroom', href: string }} ConsultationCalendlyCtaOutcomeFindShowroom
 * @typedef {ConsultationCalendlyCtaOutcomeHidden | ConsultationCalendlyCtaOutcomeRender | ConsultationCalendlyCtaOutcomeFindShowroom} ConsultationCalendlyCtaOutcome
 */

/**
 * True when the URL is an https Calendly booking URL on a trusted host (CL-011).
 *
 * @param {string} urlString
 * @returns {boolean}
 */
export function isTrustedCalendlyBookingUrl(urlString) {
  if (!urlString || typeof urlString !== 'string') {
    return false;
  }

  try {
    const u = new URL(urlString);

    if (u.protocol !== 'https:') {
      return false;
    }

    const host = u.hostname.toLowerCase();

    return host === 'calendly.com' || host.endsWith('.calendly.com');
  } catch {
    return false;
  }
}

/**
 * Value for `{{calendlyShowroomId}}` when the template already adds a `king-` path segment
 * (e.g. `https://calendly.com/king-{{calendlyShowroomId}}/...`). Store attributes are often
 * saved as the full slug (`king-osbornepark`); substituting as-is would yield `king-king-osbornepark`.
 *
 * @param {string} urlTemplate
 * @param {string} showroomId - From `c_calendlyShowroomId` (e.g. `osbornepark` or `king-osbornepark`)
 * @returns {string}
 */
export function resolveCalendlyShowroomIdForUrlTemplate(urlTemplate, showroomId) {
  const segment = String(showroomId).trim();

  if (!segment) {
    return segment;
  }

  const templateAddsKingPrefix = /king-\{\{calendlyShowroomId\}\}/i.test(urlTemplate);

  if (templateAddsKingPrefix && /^king-/i.test(segment)) {
    return segment.replace(/^king-/i, '');
  }

  return segment;
}

/**
 * Builds a booking URL from a site preference template and Calendly segment (CL-011).
 * Returns null when the resolved URL is not a trusted https Calendly host.
 *
 * @param {string} urlTemplate - e.g. https://calendly.com/king-{{calendlyShowroomId}}/...
 * @param {string} showroomId - Calendly URL segment
 * @returns {string|null}
 */
export function buildTrustedCalendlyBookingUrl(urlTemplate, showroomId) {
  if (!urlTemplate || !showroomId) {
    return null;
  }

  const segment = resolveCalendlyShowroomIdForUrlTemplate(urlTemplate, showroomId);
  const resolved = urlTemplate.replace(/\{\{calendlyShowroomId\}\}/g, segment);

  if (!isTrustedCalendlyBookingUrl(resolved)) {
    console.warn('[Calendly] Generated URL is not a trusted Calendly booking URL');

    return null;
  }

  return resolved;
}

/**
 * Returns the canonical trigger hash when the URL ends with a known Calendly trigger fragment.
 *
 * @param {string} urlString
 * @returns {typeof CALENDLY_IN_PERSON_HASH | typeof CALENDLY_VIDEO_HASH | null}
 */
export function getCalendlyTriggerHash(urlString) {
  if (!urlString || typeof urlString !== 'string') {
    return null;
  }

  const hashIndex = urlString.indexOf('#');

  if (hashIndex === -1) {
    return null;
  }

  const fragment = urlString.slice(hashIndex).split('?')[0].toLowerCase();

  if (fragment === CALENDLY_IN_PERSON_HASH) {
    return CALENDLY_IN_PERSON_HASH;
  }

  if (fragment === CALENDLY_VIDEO_HASH) {
    return CALENDLY_VIDEO_HASH;
  }

  return null;
}

/**
 * Allowed hrefs in the consultation block while Calendly is on (Contentful mistakes like "abc").
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isConsultationCalendlyCtaUrlAllowed(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmed = url.trim();

  if (!trimmed) {
    return false;
  }

  if (getCalendlyTriggerHash(trimmed)) {
    return true;
  }

  if (isTrustedCalendlyBookingUrl(trimmed)) {
    return true;
  }

  if (/^mailto:|^tel:/i.test(trimmed)) {
    return true;
  }

  if (trimmed.startsWith('/')) {
    return true;
  }

  if (trimmed.startsWith('#')) {
    return true;
  }

  return Boolean(urlHelper.parseStorefrontUrl(trimmed).appUrl);
}

/**
 * Single place for CL-011 rules: hide invalid CTAs, sanitize trigger hrefs to hash-only.
 *
 * @param {string | undefined} rawUrl - Contentful Link `url` or rich-text hyperlink URI
 * @param {{
 *   active: true,
 *   inPersonBookingAvailable: boolean,
 *   videoBookingAvailable: boolean,
 *   showFindShowroomFallback?: boolean,
 *   findShowroomHref?: string,
 * } | null | undefined} ctx
 * @returns {ConsultationCalendlyCtaOutcome}
 */
export function resolveConsultationCalendlyCta(rawUrl, ctx) {
  if (!ctx?.active) {
    return {type: 'render', url: rawUrl};
  }

  const url = typeof rawUrl === 'string' ? rawUrl : '';
  const triggerHash = getCalendlyTriggerHash(url);

  if (triggerHash) {
    if (triggerHash === CALENDLY_IN_PERSON_HASH) {
      if (ctx.inPersonBookingAvailable) {
        return {type: 'render', url: triggerHash};
      }

      if (ctx.showFindShowroomFallback && ctx.findShowroomHref) {
        return {type: 'findShowroom', href: ctx.findShowroomHref};
      }

      return {type: 'hidden'};
    }

    if (triggerHash === CALENDLY_VIDEO_HASH) {
      if (ctx.videoBookingAvailable) {
        return {type: 'render', url: triggerHash};
      }

      return {type: 'hidden'};
    }

    return {type: 'render', url: triggerHash};
  }

  if (!isConsultationCalendlyCtaUrlAllowed(url)) {
    return {type: 'hidden'};
  }

  return {type: 'render', url: rawUrl};
}

/**
 * RichTextContent `hyperlinkHrefTransform`: drop invalid links, rewrite Calendly triggers to hash-only.
 *
 * @param {{
 *   active: true,
 *   inPersonBookingAvailable: boolean,
 *   videoBookingAvailable: boolean,
 *   showFindShowroomFallback?: boolean,
 *   findShowroomHref?: string,
 * } | null | undefined} ctx
 * @returns {((uri: string) => string | null | undefined) | undefined}
 */
export function createConsultationCalendlyHyperlinkHrefTransform(ctx) {
  if (!ctx?.active) {
    return undefined;
  }

  return (uri) => {
    const outcome = resolveConsultationCalendlyCta(uri || '', ctx);

    if (outcome.type === 'hidden') {
      return null;
    }

    if (outcome.type === 'findShowroom') {
      return outcome.href;
    }

    const canonicalHash = getCalendlyTriggerHash(uri || '');

    if (canonicalHash && outcome.url === canonicalHash) {
      return canonicalHash;
    }

    return undefined;
  };
}
